import { colorFunctionalGray50, colorFunctionalWhite, colorMiscGray80, } from "@rhumbix/rmbx_design_system_design_tokens";
import React, { Component } from "react";
import Modal from "react-modal";
import styled from "styled-components";
import IconButton from "../IconButton";
/**
 * @prop {string} additionalOverlayClasses Pass in additional classes to add to the modal's overlay
 * @prop {string} ariaLabelledBy Populates the aria-labelledby field. Defaults to the title field
 * @prop {string} ariaDescribedBy Populates the aria-describedby field. Defaults to the description field
 * @prop {boolean} autoClose If this is set to false, clicking the primary button will not to close the modal. Defaults to true
 * @prop {boolean} backgroundCloseEnabled If this is set to true, clicking the background will close the modal. Defaults to false
 * @prop {function} closeMethod The callback function that triggers when the modal closes
 * @prop {string} title The title of the modal
 * @prop {string} description The subtitle of the modal. This can also serve as the body of the modal, for example in
 *                  a simple alert or error modal
 * @prop {string} primaryBtnDomId Override the ID of the primary button. Defaults to "modal-confirm"
 * @prop {string} primaryBtnColor The hex value of the primary button (which is typically the Submit button).
 *                  Defaults to colorAttentionGreen50
 * @prop {boolean} primaryBtnDisabled If this is set to true, the primary button is initially disabled. Defaults to false
 * @prop {function} primaryBtnOnClick The callback function that triggers when the primary button is clicked
 * @prop {string} primaryBtnText The label text for the primary button. Defaults to "Continue"
 * @prop {boolean} secondaryAutoClose If this is set to false, clicking the secondary button will not close the modal. Defaults to true
 * @prop {boolean} secondaryBtnDisabled If this is true, the secondary (usually cancel) button will be disabled.
 * @prop {function} secondaryBtnOnClick The callback function that triggers when the secondary button is clicked
 * @prop {string} secondaryBtnText The label text for the secondary button. Defaults to "Cancel"
 * @prop {string} height The height of the modal, specified as a string (eg "160px"). It may be better to set a minHeight
 *                  and let the modal grow to the height that its content needs
 * @prop {string} width The width of the modal
 * @prop {string} minheight The minHeight of the modal
 * @prop {string} id The id to attach to the modal
 * @prop {boolean} noButton Set this to true to hide the buttons on the modal. Defaults to false
 * @prop {boolean} open Tells the modal whether it should show or stay hidden. This may initially be set to false when the page loads and the
 *                  modal is not open, then updated to true to open the modal
 * @prop {boolean} hideSecondaryBtn Set this to true to hide the secondary button (for example, if you don't want a Cancel button).
 *                  Defaults to false
 * @prop {string} theme  Pass the name of a theme to trigger minor variations in the modal. Current options are: formModal
 * @prop {any} children The contents of the modal
 */
class StandardModal extends Component {
    static defaultProps = {
        autoClose: true,
        backgroundCloseEnabled: false,
        noButton: false,
        primaryBtnDomId: "modal-confirm",
        primaryBtnDisabled: false,
        primaryBtnText: "Continue",
        secondaryAutoClose: true,
        secondaryBtnDisabled: false,
        secondaryBtnText: "Cancel",
        hideSecondaryBtn: false,
    };
    componentWillUnmount() {
        this.props.closeMethod();
    }
    primaryBtnOnClick = () => {
        if (this.props.primaryBtnOnClick) {
            this.props.primaryBtnOnClick();
        }
        this.props.autoClose && this.props.closeMethod();
    };
    secondaryBtnOnClick = () => {
        if (this.props.secondaryBtnOnClick) {
            this.props.secondaryBtnOnClick();
        }
        this.props.secondaryAutoClose && this.props.closeMethod();
    };
    backgroundClose = () => {
        if (!this.props.backgroundCloseEnabled) {
            return;
        }
        return this.props.closeMethod();
    };
    render() {
        const { secondaryBtnText, primaryBtnText } = this.props;
        return (React.createElement(Modal, { className: "standard-modal", isOpen: this.props.open, aria: {
                labelledby: this.props.ariaLabelledBy ?? "modal_title",
                describedby: this.props.ariaDescribedBy ?? "modal_description",
            }, ariaHideApp: false, onRequestClose: this.backgroundClose, shouldCloseOnOverlayClick: this.props.backgroundCloseEnabled, overlayClassName: "Overlay" +
                (this.props.additionalOverlayClasses ? ` ${this.props.additionalOverlayClasses}` : ""), style: {
                content: {
                    width: this.props.width ?? "480px",
                    height: this.props.height ?? "undefined",
                    minHeight: this.props.minHeight ?? "160px",
                    backgroundColor: colorFunctionalWhite,
                    color: colorMiscGray80,
                    transform: `translate(-50%, -50%)`,
                    maxHeight: `calc(~"100vh - 100px")`,
                    outline: "none",
                    padding: 0,
                    overflow: "visible",
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    bottom: "auto",
                },
            } },
            React.createElement(ModalContent, { id: this.props.id },
                React.createElement(FlexibleContent, { className: "flexible-content", style: { padding: this.props.outerPadding ?? "32px 16px 16px 16px" } },
                    React.createElement(ModalTitle, { id: "modal_title" }, this.props.title),
                    React.createElement(ModalDescription, { id: "modal_description" }, this.props.description),
                    this.props.children),
                this.props.theme === "formModal" ? React.createElement(FullLengthDivider, null) : null,
                !this.props.noButton && (React.createElement(ButtonContainer, null,
                    !this.props.hideSecondaryBtn && (React.createElement(CancelButton, { onClick: this.secondaryBtnOnClick, id: "modal-cancel", text: secondaryBtnText, disabled: this.props.secondaryBtnDisabled })),
                    React.createElement(SubmitButton, { disabled: this.props.primaryBtnDisabled, onClick: this.primaryBtnOnClick, id: this.props.primaryBtnDomId, theme: "modalPrimary", style: this.props.primaryBtnColor
                            ? {
                                backgroundColor: this.props.primaryBtnColor,
                                borderColor: this.props.primaryBtnColor,
                            }
                            : {}, text: primaryBtnText }))))));
    }
}
const ModalTitle = styled.h1 `
    font-size: 20px;
    font-weight: 400;
    margin-top: 0;
    margin-bottom: 12px;
`;
const ModalDescription = styled.div `
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    margin-top: 0;
    margin-bottom: 4px;
`;
const ModalContent = styled.div `
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: inherit;
`;
const ButtonContainer = styled.div `
    margin: 0px 16px 16px 16px;
    text-align: right;
`;
const CancelButton = styled(IconButton) `
    height: 48px;
    min-width: 104px;
    padding: 2px 20px 0px;
`;
const SubmitButton = styled(IconButton) `
    height: 48px;
    min-width: 104px;
    margin-left: 8px;
    padding: 2px 20px 0px;
`;
const FlexibleContent = styled.div `
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 50px;
`;
const FullLengthDivider = styled.div `
    border-top: 1px solid ${colorFunctionalGray50};
    margin: 16px 0px;
`;
export default StandardModal;
